import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { GlobalToken } from 'antd';

import unitsEnum from '@/constants/unitsEnum';
import Super from '@/comp/Super';
import { useMeasurement } from '@/hooks/useMeasurement';
import { token } from '@/theme';

import TooltipContainer from '../StyledTooltipContainer';

type DataRow = {
  title: string;
  value: string | number;
  bulletColor?: string;
  textColor?: string;
  showZeroValue?: boolean;
};

interface StationRiskYearlyGraphTooltipProps {
  monthAvgProduction: number;
  monthPeakProduction: number;
  monthHighRisk: number;
  monthModerateRisk: number;
  monthPlannedAvailability: number;
  monthDegradation: number;
  monthLowRisk: number;
}

const StationRiskYearlyGraphTooltip = ({
  monthAvgProduction,
  monthPeakProduction,
  monthHighRisk,
  monthModerateRisk,
  monthPlannedAvailability,
  monthDegradation,
  monthLowRisk,
}: StationRiskYearlyGraphTooltipProps) => {
  const { t } = useTranslation();
  const { tvalue, tunit } = useMeasurement();

  const topSummaryRows: DataRow[] = [
    {
      title: t('AVG_PRODUCTION'),
      value: tvalue(monthAvgProduction, unitsEnum.m3Min),
      textColor: token.colorText,
    },
    {
      title: t('PEAK_PRODUCTION'),
      value: tvalue(monthPeakProduction, unitsEnum.m3Min),
      textColor: token.colorText,
    },
  ];
  const bulletRows: DataRow[] = [
    {
      title: t('SYSTEM_AVAILABILITY'),
      value: tvalue(monthHighRisk + monthModerateRisk + monthLowRisk, unitsEnum.m3Min),
      textColor: token.colorError,
      bulletColor: token.colorError,
    },
    {
      title: t('MODERATE_RISK'),
      value: tvalue(monthModerateRisk + monthLowRisk, unitsEnum.m3Min),
      textColor: token.colorWarning,
      bulletColor: token.colorWarning,
    },
    {
      title: t('LOW_RISK'),
      value: tvalue(monthLowRisk, unitsEnum.m3Min),
      textColor: token.colorSuccess,
      bulletColor: token.colorSuccess,
    },
  ];

  const bottomSummaryRows: DataRow[] = [
    {
      title: t('STATION_DEGRADATION'),
      value: tvalue(monthDegradation, unitsEnum.m3Min),
      showZeroValue: true,
      textColor: token.colorText,
    },
    {
      title: t('PLANNED_AVAILABILITY'),
      value: tvalue(monthPlannedAvailability, unitsEnum.m3Min),
      textColor: token.colorText,
    },
  ];

  const getStyledRow = data => (
    <StyledRow $textColor={data.textColor} key={data} token={token}>
      <span className="title">{data.title}</span>
      <span className="values-units-container">
        {data.value || (data.value === 0 && data.showZeroValue) ? (
          <>
            <span className="numeral-value">{data.value}</span>
            <span className="unit">
              <Trans i18nKey={tunit('m3/min')}>
                <Super />
              </Trans>
            </span>
          </>
        ) : (
          <span className="numeral-value-na">{t('NA')}</span>
        )}
      </span>
    </StyledRow>
  );

  const getBulletStyledRow = data => (
    <ColoredBulletItem $bulletColor={data.bulletColor} key={data} token={token}>
      <span className="bullet-icon">•</span>
      {getStyledRow(data)}
    </ColoredBulletItem>
  );

  return (
    <TooltipContainer className="graph-tooltip-container " token={token}>
      <div className="tooltip-highlighted-content top tooltip-summary">
        {topSummaryRows.map(data => getStyledRow(data))}
      </div>
      <div className="bullets-container">{bulletRows.map(data => getBulletStyledRow(data))}</div>
      <div className="bottom-lines">{bottomSummaryRows.map(data => getStyledRow(data))}</div>
    </TooltipContainer>
  );
};

interface StyledRowProps {
  $textColor: string;
  token: GlobalToken;
}

const StyledRow = styled.div<StyledRowProps>`
  color: ${({ $textColor }) => $textColor};
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: ${({ token }) => token.marginXXS}px 0;

  .title {
    text-transform: capitalize;
  }
`;

interface ColoredBulletItemProps {
  token: GlobalToken;
  $bulletColor: string;
}

const ColoredBulletItem = styled.span<ColoredBulletItemProps>`
  display: flex;
  align-items: center;
  width: calc(100% - 4px);

  .bullet-icon {
    font-weight: ${({ token }) => token.fontWeightStrong};
    font-size: ${({ token }) => token.fontSizeHeading3}px;
    margin-right: ${({ token }) => token.marginXS}px;
    color: ${({ $bulletColor }) => $bulletColor};
  }
`;

export default StationRiskYearlyGraphTooltip;
